/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useAtomValue } from 'jotai'
import Script from 'next/script'
import { useEffect } from 'react'

import { sessionAtom } from '@/store/session'

/**
 * Component responsible for loading the Zoho SalesIQ widget on the website.
 */
export const ZohoSalesIQ = () => {
  const sessionUser = useAtomValue(sessionAtom)
  const widgetCode = process.env.NEXT_PUBLIC_ZOHO_SALES_IQ_WIDGET_CODE
  const domain = process.env.NEXT_PUBLIC_ZOHO_SALES_IQ_DOMAIN

  /**
   * Supply the Zoho SalesIQ component with the user's session information if available.
   */
  useEffect(() => {
    updateZohoSalesIQ()
  }, [sessionUser])

  /**
   * Supply the Zoho SalesIQ component with the user's session information if available.
   */
  const updateZohoSalesIQ = () => {
    if (typeof window !== 'undefined' && (window as any).$zoho && (window as any).$zoho.salesiq) {
      if (sessionUser) {
        ;(window as any).$zoho.salesiq.visitor.name(`${sessionUser.firstName} ${sessionUser.lastName}`)
        ;(window as any).$zoho.salesiq.visitor.email(sessionUser.email)

        if (sessionUser.phone) {
          ;(window as any).$zoho.salesiq.visitor.phone(sessionUser.phone)
        }
      }
    }
  }

  // Ensure widgetCode and domain are available
  if (!widgetCode || !domain) {
    console.warn('[Zoho SalesIQ]: Widget code or domain is not configured. Widget will not load.')
    return null
  }

  return (
    <Script
      id='zoho-salesiq-script'
      strategy='lazyOnload'
      src={`https://${domain}/widget?widgetcode=${widgetCode}&plugin_source=mta-website`}
      onError={(e) => {
        console.error('[Zoho SalesIQ]: Failed to load script: ', e)
      }}
      onLoad={() => updateZohoSalesIQ()}
    />
  )
}
